import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Country, State } from 'country-state-city';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Box,
  Container,
  Grid,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Slide,
  CircularProgress,
  FormControl,
  Select,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContactsIcon from '@mui/icons-material/Contacts';
import StorageIcon from '@mui/icons-material/Storage';

// ✅ FIX: modal now talks to the service directly
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';

/* -------------------------------------------------- */
/* Slide Up Transition (Full Screen)                  */
/* -------------------------------------------------- */
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* -------------------------------------------------- */
/* Error Banner SX                                    */
/* -------------------------------------------------- */
const errorBannerSx = {
  backgroundColor: '#fee2e2',
  color: '#b91c1c',
  padding: '0.75rem 1rem',
  borderRadius: '6px',
  marginBottom: '1.5rem',
  fontSize: '0.875rem',
  borderLeft: '4px solid #dc2626',
};

/* ================================================== */
/* COMPONENT                                          */
/* ================================================== */
const CreateEditCompanyModal = ({
  open,
  company = null,
  onClose,
  onSuccess,                       // ✅ FIX: replaces `onSubmit` — called with saved doc
  existingCompanies = [],
}) => {
  const { currentUser } = useAuth();   // ✅ FIX: get the caller for the service

  const isEditMode = Boolean(company && (company.id || company.companyId));

  /* -------------------------------------------------- */
  /* Dropdown data & UI States                          */
  /* -------------------------------------------------- */
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  /* -------------------------------------------------- */
  /* Stable init ref                                    */
  /* -------------------------------------------------- */
  const initRef = useRef({ company });

  const buildInitialState = useCallback(() => {
    const { company: c } = initRef.current;
    if (!c) {
      return {
        companyId: '',
        name: '',
        address1: '',
        address2: '',
        address3: '',
        city: '',
        pincode: '',
        countryCode: 'IN',
        stateCode: '',
        phoneNumber: '',
        website: '',
        gstin: '',
        contactPersonName: '',
        contactPersonMobile: '',
        localSupabaseURL: '',
        localSupabaseAnonKey: '',
      };
    }
    return {
      companyId: c.companyId || c.id || '',
      name: c.name || '',
      address1: c.address1 || c.address || '',
      address2: c.address2 || '',
      address3: c.address3 || '',
      city: c.city || '',
      pincode: c.pincode || '',
      countryCode: c.countryCode || 'IN',
      stateCode: c.stateCode || '',
      phoneNumber: c.phoneNumber || '',
      website: c.website || '',
      gstin: c.gstin || '',
      contactPersonName: c.contactPersonName || c.contactPerson || '',
      contactPersonMobile: c.contactPersonMobile || '',
      localSupabaseURL: c.localSupabaseURL || '',
      localSupabaseAnonKey: c.localSupabaseAnonKey || '',
    };
  }, []);

  const [formData, setFormData] = useState(buildInitialState);
  const initializedRef = useRef(false);

  /* -------------------------------------------------- */
  /* Load all countries once                            */
  /* -------------------------------------------------- */
  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  /* -------------------------------------------------- */
  /* Reset form when modal opens                        */
  /* -------------------------------------------------- */
  useEffect(() => {
    if (open && !initializedRef.current) {
      initRef.current = { company };
      setFormData(buildInitialState());
      setErrors({});
      setSubmitError('');
      initializedRef.current = true;
    }
    if (!open) {
      initializedRef.current = false;
    }
  }, [open, company, buildInitialState]);

  /* -------------------------------------------------- */
  /* Load states when countryCode changes               */
  /* -------------------------------------------------- */
  useEffect(() => {
    if (formData.countryCode) {
      setLoadingStates(true);
      setStates(State.getStatesOfCountry(formData.countryCode));
      setLoadingStates(false);
    } else {
      setStates([]);
    }
  }, [formData.countryCode]);

  /* -------------------------------------------------- */
  /* Resolve country/state names to codes when editing  */
  /* -------------------------------------------------- */
  useEffect(() => {
    if (!isEditMode || !company || countries.length === 0) return;

    const alreadyResolved = countries.some(
      (c) => c.isoCode === formData.countryCode,
    );
    if (alreadyResolved && formData.stateCode) return;

    const matchedCountry =
      countries.find((c) => c.name === company.country)?.isoCode ||
      company.countryCode ||
      'IN';

    const matchedState =
      State.getStatesOfCountry(matchedCountry).find(
        (st) => st.name === company.state,
      )?.isoCode ||
      company.stateCode ||
      '';

    setFormData((prev) => ({
      ...prev,
      countryCode: matchedCountry,
      stateCode: matchedState,
    }));
  }, [isEditMode, company, countries]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -------------------------------------------------- */
  /* Input handler                                      */
  /* -------------------------------------------------- */
  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      let updates = { [name]: value };
      setSubmitError('');

      if (name === 'companyId' && !isEditMode) {
        updates.companyId = value.toUpperCase().replace(/[^A-Z0-9_-]/g, '');
      }

      if (name === 'countryCode') {
        updates.stateCode = '';
        updates.city = '';
      } else if (name === 'stateCode') {
        updates.city = '';
      }

      setFormData((prev) => ({ ...prev, ...updates }));
      setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
    },
    [isEditMode],
  );

  /* -------------------------------------------------- */
  /* Validation                                         */
  /* -------------------------------------------------- */
  const validateForm = useCallback(() => {
    const newErrors = {};
    const normalizedId = formData.companyId.trim();

    if (!normalizedId) {
      newErrors.companyId = 'Company code identifier is required';
    } else if (!isEditMode) {
      const conflict = existingCompanies.some(
        (c) =>
          (c.companyId || c.id).toLowerCase() === normalizedId.toLowerCase(),
      );
      if (conflict) {
        newErrors.companyId =
          'Conflict: This ID is already in use by another company.';
      }
    }

    if (!formData.name.trim()) newErrors.name = 'Company name is required';
    if (!formData.address1.trim()) newErrors.address1 = 'Address 1 is required';
    if (!formData.countryCode) newErrors.countryCode = 'Country is required';
    if (!formData.stateCode) newErrors.stateCode = 'State is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';

    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (
      formData.countryCode === 'IN' &&
      !/^\d{6}$/.test(formData.pincode)
    ) {
      newErrors.pincode = 'Please enter a valid 6-digit pincode';
    } else if (formData.countryCode !== 'IN' && formData.pincode.length < 3) {
      newErrors.pincode = 'Please enter a valid pincode';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (
      !/^\d{10,15}$/.test(formData.phoneNumber.replace(/[\s\-()]/g, ''))
    ) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (
      formData.contactPersonMobile &&
      !/^\d{10,15}$/.test(
        formData.contactPersonMobile.replace(/[\s\-()]/g, ''),
      )
    ) {
      newErrors.contactPersonMobile = 'Please enter a valid mobile number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isEditMode, existingCompanies]);

  /* -------------------------------------------------- */
  /* Submit — ✅ FIX: modal calls the service directly   */
  /* -------------------------------------------------- */
  const handleSubmit = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      if (!validateForm()) return;

      setIsSubmitting(true);
      setSubmitError('');
      try {
        const countryObj = countries.find(
          (c) => c.isoCode === formData.countryCode,
        );
        const stateObj = states.find((s) => s.isoCode === formData.stateCode);

        const payload = {
          ...formData,
          companyId: formData.companyId.trim(),
          id: isEditMode ? company.id : formData.companyId.trim(),
          country: countryObj ? countryObj.name : formData.countryCode,
          state: stateObj ? stateObj.name : formData.stateCode,
          address: formData.address1,
          contactPerson: formData.contactPersonName,
          email: isEditMode ? company.email || '' : '',
        };

        // ✅ FIX: call service directly — no more onSubmit prop round-trip
        let saved;
        if (isEditMode) {
          saved = await companyService.updateCompany(
            company.id,
            payload,
            currentUser,
          );
        } else {
          saved = await companyService.createCompany(payload, currentUser);
        }

        // ✅ FIX: hand the saved doc back to the parent for list refresh/notify
        if (onSuccess) onSuccess(saved || payload);
      } catch (err) {
        console.error('Error committing workspace transaction:', err);
        setSubmitError(err.message || 'Failed to save company');
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validateForm,
      countries,
      states,
      formData,
      isEditMode,
      company,
      currentUser,
      onSuccess,
    ],
  );

  /* -------------------------------------------------- */
  /* Don't render if not open                           */
  /* -------------------------------------------------- */
  if (!open) return null;

  /* ================================================== */
  /* RENDER                                             */
  /* ================================================== */
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={!isSubmitting ? onClose : undefined}
      TransitionComponent={Transition}
    >
      {/* ── FULL SCREEN APP BAR ── */}
      <AppBar sx={{ position: 'relative' }} elevation={1}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            {isEditMode
              ? `Edit Company: ${formData.name || 'Loading...'}`
              : 'Register New Company'}
          </Typography>
          <Button
            color="inherit"
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Processing...'
              : isEditMode
                ? 'Update Company'
                : 'Register Company'}
          </Button>
        </Toolbar>
      </AppBar>

      {/* ── MAIN SCROLLABLE WORKSPACE ── */}
      <Box
        sx={{
          bgcolor: 'background.default',
          py: 4,
          minHeight: 'calc(100vh - 64px)',
          overflowY: 'auto',
        }}
      >
        <Container maxWidth="xl">
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={4}>

              {/* Submit Error Banner */}
              {submitError && (
                <Grid item xs={12}>
                  <Box sx={errorBannerSx}>{submitError}</Box>
                </Grid>
              )}

              {/* ===== SECTION 1: GENERAL CORPORATE IDENTITY ===== */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={3}>
                      <BusinessIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="600">
                        General Corporate Identity
                      </Typography>
                    </Box>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Company Name *"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          error={!!errors.name}
                          helperText={errors.name}
                          placeholder="Enter company name"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Company Code *"
                          name="companyId"
                          value={formData.companyId}
                          onChange={handleChange}
                          error={!!errors.companyId}
                          helperText={
                            errors.companyId ||
                            (!isEditMode ? 'Unique alphanumeric identifier' : '')
                          }
                          placeholder="e.g., CO-MNG-01"
                          disabled={isEditMode}
                          variant={isEditMode ? 'filled' : 'outlined'}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="GSTIN"
                          name="gstin"
                          value={formData.gstin}
                          onChange={handleChange}
                          placeholder="Enter GSTIN"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Website"
                          name="website"
                          value={formData.website}
                          onChange={handleChange}
                          placeholder="www.example.com"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* ===== SECTION 2: GEOGRAPHIC LOCATION ===== */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={3}>
                      <LocationOnIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="600">
                        Geographic Location Mapping
                      </Typography>
                    </Box>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Address Line 1 *"
                          name="address1"
                          value={formData.address1}
                          onChange={handleChange}
                          error={!!errors.address1}
                          helperText={errors.address1}
                          placeholder="Plot No., Building, Street Name"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Address Line 2"
                          name="address2"
                          value={formData.address2}
                          onChange={handleChange}
                          placeholder="Industrial Area / Locality"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Address Line 3"
                          name="address3"
                          value={formData.address3}
                          onChange={handleChange}
                          placeholder="Landmarks near area"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.countryCode}
                        >
                          <Select
                            name="countryCode"
                            value={formData.countryCode || ''}
                            onChange={handleChange}
                            displayEmpty
                          >
                            <MenuItem value="">Select Country</MenuItem>
                            {countries.map((c) => (
                              <MenuItem key={c.isoCode} value={c.isoCode}>
                                {c.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.countryCode && (
                            <FormHelperText>{errors.countryCode}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.stateCode}
                        >
                          <Select
                            name="stateCode"
                            value={formData.stateCode || ''}
                            onChange={handleChange}
                            displayEmpty
                            disabled={!formData.countryCode || loadingStates}
                          >
                            <MenuItem value="">Select State</MenuItem>
                            {states.map((st) => (
                              <MenuItem key={st.isoCode} value={st.isoCode}>
                                {st.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.stateCode && (
                            <FormHelperText>{errors.stateCode}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="City *"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          error={!!errors.city}
                          helperText={errors.city}
                          placeholder="Enter city"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Pincode / Zip Code *"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          error={!!errors.pincode}
                          helperText={errors.pincode}
                          inputProps={{ maxLength: 10 }}
                          placeholder="Enter pincode"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* ===== SECTION 3: COMMUNICATION & CONTACTS ===== */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={3}>
                      <ContactsIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="600">
                        Communication Interfaces &amp; Contacts
                      </Typography>
                    </Box>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Phone Number *"
                          name="phoneNumber"
                          type="tel"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                          error={!!errors.phoneNumber}
                          helperText={errors.phoneNumber}
                          placeholder="Enter phone number"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Contact Person Name"
                          name="contactPersonName"
                          value={formData.contactPersonName}
                          onChange={handleChange}
                          placeholder="Enter contact person name"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Contact Person Mobile"
                          name="contactPersonMobile"
                          type="tel"
                          value={formData.contactPersonMobile}
                          onChange={handleChange}
                          error={!!errors.contactPersonMobile}
                          helperText={errors.contactPersonMobile}
                          placeholder="Enter mobile number"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* ===== SECTION 4: LOCAL INFRASTRUCTURE ===== */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={3}>
                      <StorageIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="600">
                        Local Server &amp; Database Configuration
                      </Typography>
                    </Box>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Local Supabase URL"
                          name="localSupabaseURL"
                          value={formData.localSupabaseURL}
                          onChange={handleChange}
                          placeholder="http://localhost:54321 or LAN IP"
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: '#9ca3af', fontStyle: 'italic', mt: 0.5, display: 'block' }}
                        >
                          Leave blank if not using local Supabase instance
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Local Supabase Anon Key"
                          name="localSupabaseAnonKey"
                          value={formData.localSupabaseAnonKey}
                          onChange={handleChange}
                          placeholder="Enter public anonymous API key"
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: '#9ca3af', fontStyle: 'italic', mt: 0.5, display: 'block' }}
                        >
                          Public anonymous key for your local Supabase gateway
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

            </Grid>
          </Box>
        </Container>
      </Box>
    </Dialog>
  );
};

export default CreateEditCompanyModal;